import React from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  Globe,
  Smartphone,
  Zap,
  CheckCircle2,
  ArrowUpRight,
  Search,
  ShoppingBag,
  Code2,
  Gauge,
  Lock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';
import ServicePricing, { PricingTier } from '@/components/service/ServicePricing';
import ServiceFAQ, { FAQItem } from '@/components/service/ServiceFAQ';
import ServiceCrossSell from '@/components/service/ServiceCrossSell';
import ServiceBreadcrumb from '@/components/service/ServiceBreadcrumb';

const ServicesWeb: React.FC = () => {
  const { t, language } = useLanguage();
  const isPT = language === 'pt';

  const features = [
    { Icon: Code2,      key: 'modern' },
    { Icon: Smartphone, key: 'responsive' },
    { Icon: Zap,        key: 'fast' },
  ] as const;

  const benefitsRaw = t('servicesWeb.benefits') as unknown;
  const benefits: string[] = Array.isArray(benefitsRaw) ? (benefitsRaw as string[]) : [];

  const title = isPT
    ? 'Websites Profissionais para Pequenas Empresas | Spawncore'
    : 'Professional Websites for Small Businesses | Spawncore';

  const description = isPT
    ? 'Criação de websites profissionais, responsivos e otimizados para Google. Entrega em 5 dias, com domínio, hospedagem e SSL incluídos. Para pequenas empresas em Portugal.'
    : 'Professional, responsive, Google-ready websites. Delivered in 5 days, with domain, hosting and SSL included. For small businesses in Portugal.';

  const tiers: PricingTier[] = isPT ? [
    {
      name: 'Starter',
      price: '€150',
      description: 'Uma página profissional para começar a aparecer no Google.',
      features: [
        'Site one-page profissional',
        'Design mobile-first responsivo',
        'Domínio e hospedagem 1 ano',
        'Certificado SSL incluído',
        'Formulário de contacto',
        'Configuração SEO básico',
        'Entrega em 5 dias',
      ],
      ctaLabel: 'Começar starter',
    },
    {
      name: 'Pro',
      price: '€450',
      description: 'Site completo com múltiplas páginas e serviços detalhados.',
      features: [
        'Até 6 páginas personalizadas',
        'Blog ou portfólio (opcional)',
        'Integração Google Analytics',
        'Otimização SEO técnico + on-page',
        'Conteúdo redigido por nós',
        'Animações e micro-interações',
        'Formulários avançados',
        'Manutenção 3 meses incluída',
      ],
      popular: true,
      ctaLabel: 'Escolher Pro',
    },
    {
      name: 'Commerce',
      price: 'Desde €1200',
      description: 'Loja online ou site com integrações avançadas.',
      features: [
        'Loja online completa',
        'Gestão de produtos e stock',
        'Pagamentos (Stripe, MB Way, etc.)',
        'Integração com IA / chatbot',
        'CRM e automações',
        'Multi-idioma (PT/EN)',
        'Painel de gestão',
        'Suporte prioritário 6 meses',
      ],
      ctaLabel: 'Falar com o estúdio',
    },
  ] : [
    {
      name: 'Starter',
      price: '€150',
      description: 'A single professional page to start showing up on Google.',
      features: [
        'One-page professional site',
        'Mobile-first responsive design',
        'Domain and 1-year hosting',
        'SSL certificate included',
        'Contact form',
        'Basic SEO setup',
        'Delivered in 5 days',
      ],
      ctaLabel: 'Start with Starter',
    },
    {
      name: 'Pro',
      price: '€450',
      description: 'Full site with multiple pages and service detail.',
      features: [
        'Up to 6 custom pages',
        'Blog or portfolio (optional)',
        'Google Analytics integration',
        'Technical + on-page SEO',
        'Copy written by us',
        'Animations and micro-interactions',
        'Advanced forms',
        '3 months of maintenance included',
      ],
      popular: true,
      ctaLabel: 'Choose Pro',
    },
    {
      name: 'Commerce',
      price: 'From €1200',
      description: 'Online store or site with advanced integrations.',
      features: [
        'Full online store',
        'Product and stock management',
        'Payments (Stripe, MB Way, etc.)',
        'AI / chatbot integration',
        'CRM and automations',
        'Multi-language (PT/EN)',
        'Admin panel',
        '6 months priority support',
      ],
      ctaLabel: 'Talk to the studio',
    },
  ];

  const useCases = isPT ? [
    { Icon: Globe,        title: 'Restaurantes',    desc: 'Menu sempre atualizado, reservas online e mapa otimizado para mobile.' },
    { Icon: ShoppingBag,  title: 'Lojas locais',    desc: 'Vitrine online com fotos, preços e botão directo para WhatsApp ou loja.' },
    { Icon: Code2,        title: 'Profissionais',   desc: 'Páginas focadas em portfolio, testemunhos e marcação de consultas.' },
    { Icon: Gauge,        title: 'Serviços B2B',    desc: 'Site institucional com casos de estudo, blog e captura de leads.' },
    { Icon: Search,       title: 'Imobiliárias',    desc: 'Listagens dinâmicas, mapa, filtros e contacto rápido por imóvel.' },
    { Icon: Lock,         title: 'Áreas de membros',desc: 'Conteúdo privado, login seguro e gestão de subscrições.' },
  ] : [
    { Icon: Globe,        title: 'Restaurants',     desc: 'Live menu, online reservations and mobile-friendly map.' },
    { Icon: ShoppingBag,  title: 'Local shops',     desc: 'Online window with photos, prices and direct WhatsApp/shop button.' },
    { Icon: Code2,        title: 'Professionals',   desc: 'Pages focused on portfolio, testimonials and appointment booking.' },
    { Icon: Gauge,        title: 'B2B services',    desc: 'Institutional site with case studies, blog and lead capture.' },
    { Icon: Search,       title: 'Real estate',     desc: 'Dynamic listings, map, filters and per-property contact.' },
    { Icon: Lock,         title: 'Member areas',    desc: 'Private content, secure login and subscription management.' },
  ];

  const techStack = isPT ? [
    { name: 'React',           desc: 'Interfaces modernas' },
    { name: 'Next.js / Astro', desc: 'Performance & SEO' },
    { name: 'Tailwind',        desc: 'Design responsivo' },
    { name: 'Vercel / CF',     desc: 'CDN global' },
    { name: 'Sanity / Strapi', desc: 'CMS sem complicações' },
    { name: 'Stripe / MB Way', desc: 'Pagamentos' },
  ] : [
    { name: 'React',           desc: 'Modern interfaces' },
    { name: 'Next.js / Astro', desc: 'Performance & SEO' },
    { name: 'Tailwind',        desc: 'Responsive design' },
    { name: 'Vercel / CF',     desc: 'Global CDN' },
    { name: 'Sanity / Strapi', desc: 'Hassle-free CMS' },
    { name: 'Stripe / MB Way', desc: 'Payments' },
  ];

  const faqs: FAQItem[] = isPT ? [
    {
      question: 'Em quanto tempo o meu site fica online?',
      answer: 'O pacote Starter fica pronto em 5 dias úteis após aprovação do conteúdo. O Pro entre 10-15 dias. Loja online (Commerce) tipicamente 3-4 semanas. Trabalhamos por sprints curtos com pontos de aprovação claros — sem desaparecer a meio.',
    },
    {
      question: 'Vou poder atualizar o site sozinho?',
      answer: 'Sim. Configuramos um CMS simples (Sanity ou similar) para que possa editar textos, imagens e adicionar artigos do blog sem mexer em código. Se preferir, oferecemos um plano de manutenção em que tratamos das atualizações por si.',
    },
    {
      question: 'Qual a diferença para Wix ou Squarespace?',
      answer: 'Plataformas no-code são óptimas para começar, mas crescem mal: limitam SEO, são lentas, e ficam caras quando precisa de algo personalizado. Construímos sites com tecnologia profissional (React, Next.js) — mais rápidos no Google, escaláveis, e seus desde o primeiro dia. Sem mensalidades de plataforma.',
    },
    {
      question: 'O site vai aparecer no Google?',
      answer: 'Configuramos SEO técnico de origem: metadados, schema.org, sitemap, performance otimizada e Core Web Vitals altos. Para resultados orgânicos consistentes, recomendamos combinar com o nosso serviço de SEO & Ads.',
    },
    {
      question: 'Posso integrar com um chatbot IA?',
      answer: 'Sim. Todos os nossos sites preparam o terreno para integrações IA. No pacote Commerce já incluímos um chatbot básico. Para automações mais avançadas (qualificação de leads, WhatsApp 24/7, agendamento automático) ver Automação IA.',
    },
    {
      question: 'E se eu já tiver um site?',
      answer: 'Podemos modernizar o que já tem ou começar de novo. Após uma chamada gratuita avaliamos o que vale a pena reaproveitar — domínio, conteúdo, branding — e recomendamos o caminho mais económico.',
    },
  ] : [
    {
      question: 'How fast does my site go live?',
      answer: 'Starter is ready in 5 working days after content approval. Pro takes 10-15 days. Commerce typically 3-4 weeks. Short sprints with clear approval gates — no disappearing mid-project.',
    },
    {
      question: 'Can I update the site myself?',
      answer: 'Yes. We set up a simple CMS (Sanity or similar) so you can edit text, images and add blog posts without touching code. If you prefer, our maintenance plan covers it for you.',
    },
    {
      question: 'How does this compare to Wix or Squarespace?',
      answer: 'No-code platforms are great to start, but they scale poorly: limited SEO, slow, and expensive once you need anything custom. We build with professional tech (React, Next.js) — faster on Google, scalable, and yours from day one. No platform subscription.',
    },
    {
      question: 'Will it rank on Google?',
      answer: 'Technical SEO is baked in: metadata, schema.org, sitemap, optimized performance and strong Core Web Vitals. For consistent organic results, pair it with our SEO & Ads service.',
    },
    {
      question: 'Can I add an AI chatbot?',
      answer: 'Yes. Every site is built ready for AI integrations. Commerce already includes a basic chatbot. For advanced automation (lead qualification, 24/7 WhatsApp, auto-scheduling) see AI Automation.',
    },
    {
      question: 'I already have a website. Now what?',
      answer: 'We can modernize what you have or start fresh. After a free call we audit what\'s worth keeping — domain, content, branding — and recommend the most cost-effective path.',
    },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Web Development',
    name: isPT ? 'Desenvolvimento de Websites Profissionais' : 'Professional Website Development',
    description,
    provider: { '@type': 'Organization', name: 'Spawncore', url: 'https://spawncore.com' },
    areaServed: 'PT',
    audience: { '@type': 'BusinessAudience', audienceType: 'Small Business' },
    url: 'https://spawncore.com/services/web',
    offers: tiers.map(t => ({
      '@type': 'Offer',
      name: t.name,
      price: t.price.replace(/[^\d]/g, '') || undefined,
      priceCurrency: 'EUR',
      description: t.description,
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={isPT ? 'pt' : 'en'} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={isPT
          ? 'websites profissionais Portugal, criação de sites Azambuja, web design pequenas empresas, sites baratos, desenvolvimento web, sites responsivos, loja online Portugal, e-commerce, sites em 5 dias'
          : 'professional websites Portugal, small business web design, custom websites, web development, responsive sites, online store Portugal, e-commerce, websites in 5 days'} />
        <link rel="canonical" href="https://spawncore.com/services/web" />
        <link rel="alternate" hrefLang="pt-PT" href="https://spawncore.com/services/web" />
        <link rel="alternate" hrefLang="en" href="https://spawncore.com/services/web" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com/services/web" />
        <meta property="og:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />
        <meta property="og:locale" content={isPT ? 'pt_PT' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <SafeJsonLd data={serviceSchema} />

      <header>
        <Navigation />
      </header>

      <ServiceBreadcrumb label={t('nav.web')} url="https://spawncore.com/services/web" />

      <main>
        {/* Hero */}
        <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-60" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <span className="pill-tag mb-8">
              <Globe className="h-3 w-3" />
              {isPT ? 'Websites · Para pequenas empresas' : 'Websites · For small business'}
            </span>
            <h1 className="font-display text-foreground text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.96] tracking-tight">
              {isPT ? 'A sua nova ' : 'Your new '}
              <span className="italic font-display text-primary">
                {isPT ? 'montra digital' : 'digital storefront'}
              </span>
              {isPT ? ' em 5 dias.' : ' in 5 days.'}
            </h1>
            <p className="mt-7 text-lg md:text-2xl text-muted-foreground max-w-2xl leading-relaxed">
              {t('servicesWeb.subtitle')}
            </p>
            <p className="mt-5 text-base text-muted-foreground/80 max-w-2xl leading-relaxed">
              {isPT
                ? 'Websites construídos com tecnologia profissional (React, Next.js), otimizados para Google e mobile, prontos a converter visitantes em clientes. Domínio, hospedagem e SSL incluídos.'
                : 'Websites built with professional tech (React, Next.js), optimized for Google and mobile, ready to convert visitors into customers. Domain, hosting and SSL included.'}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="group h-14 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
                >
                  <Globe className="mr-2 h-4 w-4" />
                  {t('servicesWeb.cta')}
                  <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="#pricing" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-7 rounded-full bg-card/40 border-border hover:bg-card text-base w-full sm:w-auto"
                >
                  {isPT ? 'Ver preços' : 'See pricing'}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesWeb.whyUs')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Três princípios não negociáveis.' : 'Three non-negotiable principles.'}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {features.map(({ Icon, key }, idx) => (
                <article
                  key={key}
                  className="group relative overflow-hidden rounded-2xl border border-border/80 bg-card/70 backdrop-blur-sm p-8 transition-all duration-500 hover:bg-card hover:border-primary/40 hover:-translate-y-1"
                >
                  <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-primary/[0.10] blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-secondary text-primary">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <span className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                        / 0{idx + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-[1.625rem] leading-tight tracking-tight mb-3 text-foreground">
                      {t(`servicesWeb.features.${key}.title`)}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {t(`servicesWeb.features.${key}.desc`)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-20 md:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-12 gap-6 mb-14">
              <div className="col-span-12 md:col-span-7">
                <span className="eyebrow">— {isPT ? 'Para quem é' : 'Who it\'s for'}</span>
                <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                  {isPT ? 'Websites desenhados para o seu negócio.' : 'Websites designed for your business.'}
                </h2>
              </div>
              <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
                {isPT ? 'Cada setor tem o que precisa mostrar — e o que precisa esconder.' : 'Each sector knows what to show — and what to hide.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, idx) => (
                <article
                  key={uc.title}
                  className="group rounded-xl border border-border/80 bg-card/60 p-6 hover:bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-secondary text-primary">
                      <uc.Icon className="h-4 w-4" strokeWidth={1.6} />
                    </div>
                    <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                      / 0{idx + 1}
                    </div>
                  </div>
                  <h3 className="font-display text-xl text-foreground mb-2.5 leading-tight">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits / Included */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesWeb.included')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'O que vem sempre incluído.' : 'What\'s always included.'}
              </h2>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-5">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-4 group">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/[0.10] text-primary group-hover:bg-primary/[0.20] transition-colors">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <span className="text-foreground/90 text-base md:text-lg leading-relaxed pt-0.5">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Tech stack */}
        <section className="py-20 md:py-24 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'Stack' : 'Stack'}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Tecnologia profissional. Sem mensalidades de plataforma.' : 'Professional tech. No platform subscriptions.'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60">
              {techStack.map(({ name, desc }) => (
                <div key={name} className="bg-card/60 p-5 hover:bg-card transition-colors">
                  <div className="h-1 w-6 bg-primary mb-3 rounded-full" />
                  <div className="font-display text-sm text-foreground leading-tight">{name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <div id="pricing">
          <ServicePricing
            eyebrow={isPT ? 'Investimento' : 'Investment'}
            heading={isPT ? 'Preço fixo. Sem surpresas.' : 'Fixed price. No surprises.'}
            subtitle={isPT
              ? 'Os preços abaixo são pontos de partida. Após a chamada gratuita confirmamos um preço fixo escrito.'
              : 'These are starting points. After the free call we confirm a fixed written quote.'}
            tiers={tiers}
            note={isPT
              ? 'Hospedagem, domínio e SSL incluídos no 1º ano. A partir do 2º ano: €60/ano.'
              : 'Hosting, domain and SSL included in year one. From year two: €60/year.'}
          />
        </div>

        {/* FAQ */}
        <ServiceFAQ
          eyebrow={isPT ? 'Perguntas frequentes · Websites' : 'FAQ · Websites'}
          heading={isPT ? 'O que costumam perguntar.' : 'What people ask.'}
          subtitle={isPT ? 'Respostas diretas.' : 'Straight answers.'}
          faqs={faqs}
        />

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-50" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <span className="pill-tag mb-6">
              <Globe className="h-3 w-3" />
              {isPT ? 'Chamada gratuita · 30 min' : 'Free call · 30 min'}
            </span>
            <h2 className="font-display text-foreground text-[clamp(2rem,5vw,4rem)] leading-[1.02] tracking-tight mb-6">
              {t('servicesWeb.ready')}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('servicesWeb.readyText')}
            </p>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="group h-14 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
              >
                <Globe className="mr-2 h-4 w-4" />
                {t('servicesWeb.contact')}
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </section>

        <ServiceCrossSell current="web" />
      </main>

      <Footer />
    </div>
  );
};

export default ServicesWeb;
